import { z } from 'zod';

export const infoCardSchema = z.object({ title: z.string(), text: z.string() });

export const profileSchema = z
  .object({
    tagline: z.string().default(''),
    description: z.string().default(''),
    address: z.string().default(''),
    phone: z.string().default(''),
    map_url: z.string().nullable().optional(),
    info_cards: z.array(infoCardSchema).default([]),
    accent: z.string().default('#4690FF'),
    background: z.string().default('#0F1114'),
    short_name: z.string().optional(),
  })
  .loose();

export const publicServiceSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  price_cents: z.number(),
  price_is_from: z.boolean(),
  duration_minutes: z.number(),
  photo_path: z.string().nullable(),
  resource_count: z.number(),
});

export const photoSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['hero', 'logo', 'work', 'service']),
  path: z.string(),
  caption: z.string(),
  service_id: z.uuid().nullable().optional(),
  source: z.enum(['config', 'owner']).optional(),
  sort: z.number().optional(),
});

export const hoursSchema = z.object({ weekday: z.number(), opens: z.string(), closes: z.string() });
export const exceptionSchema = z.object({
  day: z.string(),
  is_closed: z.boolean(),
  opens: z.string().nullable(),
  closes: z.string().nullable(),
  note: z.string(),
});

export const publicTenantSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(['preview', 'live', 'suspended']),
  timezone: z.string(),
  currency: z.string(),
  slot_step_minutes: z.number(),
  cancel_cutoff_minutes: z.number(),
  booking_horizon_days: z.number(),
  profile: profileSchema,
  services: z.array(publicServiceSchema),
  photos: z.array(photoSchema),
  hours: z.array(hoursSchema),
  exceptions: z.array(exceptionSchema),
});

export const slotSchema = z.object({
  day: z.string(),
  starts_at: z.string(),
  time: z.string(),
  available: z.boolean(),
});

export const bookingViewSchema = z.object({
  id: z.uuid(),
  status: z.enum(['confirmed', 'arrived', 'completed', 'cancelled', 'no_show']),
  service_id: z.uuid(),
  service_name: z.string(),
  price_cents: z.number(),
  price_is_from: z.boolean(),
  duration_minutes: z.number(),
  starts_at: z.string(),
  work_ends_at: z.string(),
  resource_name: z.string(),
  customer_name: z.string(),
  customer_phone: z.string(),
  car: z.string(),
  comment: z.string(),
  is_demo: z.boolean(),
  cancelled_at: z.string().nullable(),
  cancel_deadline: z.string(),
  can_change: z.boolean(),
  tenant: z.object({
    slug: z.string(),
    name: z.string(),
    timezone: z.string(),
    status: z.string(),
    cancel_cutoff_minutes: z.number(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
  }),
});

export type PublicTenant = z.infer<typeof publicTenantSchema>;
export type PublicService = z.infer<typeof publicServiceSchema>;
export type Slot = z.infer<typeof slotSchema>;
export type BookingView = z.infer<typeof bookingViewSchema>;
export type Photo = z.infer<typeof photoSchema>;
export type Profile = z.infer<typeof profileSchema>;
